/* import __COLOCATED_TEMPLATE__ from './smart-suggestions-section.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import Component from '@glimmer/component';
import type { SuggestionGroup } from './types';

interface Suggestion {
  id: number;
  text: string;
  display_priority: number;
}

interface GenerationJob {
  job_id: number;
  status: string;
  website_url: string;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
}

interface Args {
  smartSuggestionsEnabled: boolean;
  suggestions: Suggestion[];
  showAllSuggestions: boolean;
  isGeneratingSuggestions: boolean;
  isEditingWebsiteUrl: boolean;
  generationJob: GenerationJob | null;
  websiteUrl: string;
  onToggle: () => void;
  onToggleShowAll: () => void;
  onUpdateWebsiteUrl: (event: Event) => void;
  onGenerate: () => void;
  onStartEditingUrl: () => void;
  onCancelEditingUrl: () => void;
  suggestionGroups: SuggestionGroup[];
  onRemoveSuggestion: (groupUuid: string) => void;
  failedGenerationUrl: string;
  failedGenerationErrorMessage: string;
  isGenerationErrorDismissed: boolean;
  onDismissGenerationError: () => void;
  onRetryFailedGeneration: () => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class SmartSuggestionsSection extends Component<Signature> {
  get hasGroupData(): boolean {
    return this.args.suggestionGroups.length > 0;
  }

  get displayedGroups(): SuggestionGroup[] {
    if (this.args.showAllSuggestions) {
      return this.args.suggestionGroups;
    }
    return this.args.suggestionGroups.slice(0, 3);
  }

  get hasMoreSuggestions(): boolean {
    return this.args.suggestionGroups.length > 3;
  }

  get remainingCount(): number {
    return this.args.suggestionGroups.length - 3;
  }

  get hasNotEnoughSuggestions(): boolean {
    return this.args.suggestionGroups.length > 0 && this.args.suggestionGroups.length < 3;
  }

  get hasGenerationError(): boolean {
    return Boolean(this.args.failedGenerationUrl && !this.args.isGenerationErrorDismissed);
  }

  get isErrorDismissible(): boolean {
    return this.hasGroupData;
  }

  get failedDisplayUrl(): string {
    return this.args.failedGenerationUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  }

  get hasCompletedUrl(): boolean {
    let job = this.args.generationJob;
    return Boolean(
      job && (job.status === 'completed' || job.status === 'failed') && job.website_url,
    );
  }

  get showUrlDisplay(): boolean {
    return (
      this.hasCompletedUrl && !this.args.isEditingWebsiteUrl && !this.args.isGeneratingSuggestions
    );
  }

  get showUrlInput(): boolean {
    if (this.args.isGeneratingSuggestions) {
      return false;
    }
    return !this.hasCompletedUrl || this.args.isEditingWebsiteUrl;
  }

  get showCancelButton(): boolean {
    return this.args.isEditingWebsiteUrl && this.hasCompletedUrl;
  }

  get displayUrl(): string {
    return this.args.websiteUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  }

  get isGenerateDisabled(): boolean {
    return !this.args.websiteUrl.trim() || this.args.isGeneratingSuggestions;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'MessengerSettings::Sales::SmartSuggestionsSection': typeof SmartSuggestionsSection;
  }
}
